import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, History, Loader2 } from "lucide-react";
import { getAgeingLoanDiskTimeline } from "@/lib/api";

const TONE = {
  Open: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Processing: "bg-slate-50 text-slate-700 border-slate-200",
  Legal: "bg-rose-50 text-rose-800 border-rose-200",
  Defaulted: "bg-rose-50 text-rose-800 border-rose-200",
  Restructured: "bg-amber-50 text-amber-800 border-amber-200",
  "Fully Paid": "bg-sky-50 text-sky-800 border-sky-200",
  Denied: "bg-slate-100 text-slate-600 border-slate-200",
  "Not Taken Up": "bg-slate-100 text-slate-600 border-slate-200",
};

export function LoanDiskTimelineStrip({ caseId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null); setErr(null);
    getAgeingLoanDiskTimeline(caseId)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || e.message); });
    return () => { cancelled = true; };
  }, [caseId]);

  const entries = data?.entries || [];
  const latest = entries[entries.length - 1];
  const shown = open ? [...entries].reverse() : [];

  return (
    <div className="mt-2 pt-2 border-t border-blue-100/80 text-[11px]" data-testid="customer-file-ld-timeline">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-[9px] uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><History className="w-3 h-3" /> LoanDisk status</span>
        {!data && !err && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" data-testid="customer-file-ld-timeline-loading" />}
        {err && <span className="text-amber-700" data-testid="customer-file-ld-timeline-error">{err}</span>}
        {data && !latest && <span className="text-muted-foreground" data-testid="customer-file-ld-timeline-empty">No status history on LoanDisk yet</span>}
        {latest && (
          <span className="inline-flex items-center gap-1.5" data-testid="customer-file-ld-timeline-latest">
            <span className={`px-1.5 py-0.5 rounded border font-medium ${TONE[latest.status] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
              {latest.status}{latest.sub_status ? ` · ${latest.sub_status}` : ""}{latest.open_label_to ? ` · ${latest.open_label_to}` : ""}
            </span>
            <span className="text-slate-500">since {latest.at}{latest.by ? ` · by ${latest.by}` : ""}</span>
          </span>
        )}
        {entries.length > 0 && (
          <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] ml-auto" onClick={() => setOpen((v) => !v)} data-testid="customer-file-ld-timeline-toggle">
            {entries.length} change{entries.length === 1 ? "" : "s"} {open ? <ChevronUp className="w-3 h-3 ml-0.5" /> : <ChevronDown className="w-3 h-3 ml-0.5" />}
          </Button>
        )}
      </div>
      {open && (
        <ol className="mt-1.5 space-y-0.5 max-h-48 overflow-y-auto pr-1" data-testid="customer-file-ld-timeline-list">
          {shown.map((e, i) => (
            <li key={i} className="flex items-baseline gap-2 text-slate-700" data-testid="customer-file-ld-timeline-entry">
              <span className="font-mono text-[10px] text-slate-500 w-[118px] shrink-0">{e.at}</span>
              <span className={`px-1 rounded border text-[10px] ${TONE[e.status] || "bg-slate-50 border-slate-200"}`}>{e.status}</span>
              {e.sub_status && <span className="text-slate-600">{e.sub_status}</span>}
              {e.open_label_to && <span className="text-indigo-700">label → {e.open_label_to}</span>}
              {e.note && <span className="text-slate-500 italic truncate">{e.note}</span>}
              <span className="ml-auto text-slate-400 text-[10px] shrink-0">{e.by || (e.changed_by === "system" ? "LoanDisk" : "")}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
